// @ts-strict-ignore
import { Box3, BufferAttribute, BufferGeometry, Matrix4, Object3D, Plane, Quaternion, Vector2, Vector3 } from 'three';
import { NumberUtils } from '@shared/util/number-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { Rectangle3D } from '../../model/rectangle-3d';
import { CommonConstants } from '../../constants/common-constants';
import { PlaneClassification } from '../../model/plane-classification';
import { Line } from '../../model/line';
import { Face3, GeometryUtils } from './geometry-utils';

export class MathUtils {

  public static readonly DEFAULT_CONNECTOR_DISTANCE: number = 10;

  public static readonly TO_RADIANS: number = Math.PI / 180;
  public static readonly TO_DEGREES: number = 180 / Math.PI;

  public static toDegrees(radian: number): number {
    if (!NumberUtils.isNumber(radian)) {
      return NaN;
    }
    return radian * MathUtils.TO_DEGREES;
  }

  public static toRadians(degree: number): number {
    if (!NumberUtils.isNumber(degree)) {
      return NaN;
    }
    return degree * MathUtils.TO_RADIANS;
  }

  /** remaps a value from one range of numbers to another range. See https://docs.arduino.cc/language-reference/en/functions/math/map/ */
  public static mapRange(value: number, fromMin: number, fromMax: number, toMin: number, toMax: number): number {
    const mapped: number = ((value - fromMin) * (toMax - toMin)) / (fromMax - fromMin) + toMin;
    return toMin < toMax ? MathUtils.clamp(mapped, toMin, toMax) : MathUtils.clamp(mapped, toMax, toMin);
  }

  public static radiansToDegrees(radians: Vector3): Vector3 {
    return new Vector3(radians.x * MathUtils.TO_DEGREES, radians.y * MathUtils.TO_DEGREES, radians.z * MathUtils.TO_DEGREES);
  }

  public static degreesToRadians(degrees: Vector3): Vector3 {
    return new Vector3(degrees.x * MathUtils.TO_RADIANS, degrees.y * MathUtils.TO_RADIANS, degrees.z * MathUtils.TO_RADIANS);
  }

  public static calculateBoundingUVRectangle(geometry: BufferGeometry, scale?: Vector3): Rectangle3D {
    if (!geometry || !geometry.attributes.position || !geometry.attributes.uv) {
      return null;
    }

    // Nur das erste Dreieck verwenden
    const positionAttribute = geometry.attributes.position as BufferAttribute;
    const uvAttribute = geometry.attributes.uv as BufferAttribute;

    if (positionAttribute.count < 3 || uvAttribute.count < 3) {
      return null;
    }

    const points: Vector3[] = [];
    const uvs: Vector3[] = [];

    let scaleTransform: Matrix4 = null;
    if (scale) {
      scaleTransform = new Matrix4().makeScale(scale.x, scale.y, scale.z);
    }

    for (let i = 0; i < 3; i++) {
      let v = new Vector3(positionAttribute.getX(i), positionAttribute.getY(i), positionAttribute.getZ(i));
      if (scaleTransform) {
        v = v.clone().applyMatrix4(scaleTransform);
      }
      points.push(v);

      const uvVec = new Vector2(uvAttribute.getX(i), uvAttribute.getY(i));
      uvs.push(new Vector3(uvVec.x, uvVec.y, 1));
    }

    const uvTransform: Matrix4 = this.getTransformationMatrix(uvs, points);
    const rect: Rectangle3D = new Rectangle3D();
    rect.point1 = new Vector3(0, 0, 1).applyMatrix4(uvTransform);
    rect.point2 = new Vector3(1, 0, 1).applyMatrix4(uvTransform);
    rect.point3 = new Vector3(1, 1, 1).applyMatrix4(uvTransform);
    rect.point4 = new Vector3(0, 1, 1).applyMatrix4(uvTransform);
    return rect;
  }

  public static getTransformationMatrix(fromFace: Vector3[], toFace: Vector3[]): Matrix4 {
    if (toFace.length !== 3 || fromFace.length !== 3) {
      return null;
    }
    return this.getTransformation(this.pointsToMatrix4x3(fromFace), this.pointsToMatrix4x3(toFace));
  }

  private static getTransformation(fromMatrix: Matrix4, toMatrix: Matrix4): Matrix4 {
    const inverseMatrix: Matrix4 = fromMatrix.clone().invert();
    const resultMatrix: Matrix4 = toMatrix.clone();
    resultMatrix.multiply(inverseMatrix);
    return resultMatrix;
  }

  public static pointsToMatrix4x3(face: Vector3[]): Matrix4 {
    return this.pointsToMatrix4x4(face, new Vector3(0, 0, 0));
  }

  public static pointsToMatrix4x4(face: Vector3[], position: Vector3): Matrix4 {
    const matrix: Matrix4 = new Matrix4();
    const p1: Vector3 = face[0];
    const p2: Vector3 = face[1];
    const p3: Vector3 = face[2];

    matrix.makeBasis(p1, p2, p3);
    matrix.setPosition(position);
    return matrix;
  }

  public static getInterpolated(w: Vector3, v: Vector3, f: number): Vector3 {
    const d: Vector3 = w.clone().sub(v);
    d.multiplyScalar(f);
    d.add(v);
    return d;
  }

  public static calculateMiddlePointByRectangle(rectangle: Rectangle3D): Vector3 {
    return new Vector3((rectangle.point1.x + rectangle.point3.x) / 2,
      (rectangle.point1.y + rectangle.point3.y) / 2, (rectangle.point1.z + rectangle.point3.z) / 2);
  }

  public static constructBasicScaleMatrix(startPoint: Vector3, endPoint: Vector3, scaleEndPoint: Vector3,
                                          roundedScalePoints: boolean = false, epsilon: number = NaN): Matrix4 {
    if (!NumberUtils.isNumber(epsilon)) {
      epsilon = CommonConstants.EPSILON;
    }
    let numeratorScale: number = scaleEndPoint.x - startPoint.x;
    if (roundedScalePoints && Math.abs(numeratorScale) < epsilon) {
      numeratorScale = NaN;
    }
    let denominatorScale: number = endPoint.x - startPoint.x;
    if (roundedScalePoints && Math.abs(denominatorScale) < epsilon) {
      denominatorScale = NaN;
    }
    const scaleX: number = numeratorScale / denominatorScale;

    numeratorScale = scaleEndPoint.y - startPoint.y;
    if (roundedScalePoints && Math.abs(numeratorScale) < epsilon) {
      numeratorScale = NaN;
    }
    denominatorScale = endPoint.y - startPoint.y;
    if (roundedScalePoints && Math.abs(denominatorScale) < epsilon) {
      denominatorScale = NaN;
    }
    const scaleY: number = numeratorScale / denominatorScale;

    numeratorScale = scaleEndPoint.z - startPoint.z;
    if (roundedScalePoints && Math.abs(numeratorScale) < epsilon) {
      numeratorScale = NaN;
    }
    denominatorScale = endPoint.z - startPoint.z;
    if (roundedScalePoints && Math.abs(denominatorScale) < epsilon) {
      denominatorScale = NaN;
    }
    const scaleZ: number = numeratorScale / denominatorScale;

    const scaleMatrix: Matrix4 = new Matrix4();
    scaleMatrix.set((!NumberUtils.isNumber(scaleX) || scaleX === 0) ? 1 : scaleX, 0, 0, 0,
      0, (!NumberUtils.isNumber(scaleY) || scaleY === 0) ? 1 : scaleY, 0, 0,
      0, 0, (!NumberUtils.isNumber(scaleZ) || scaleZ === 0) ? 1 : scaleZ, 0,
      0, 0, 0, 1);
    return scaleMatrix;
  }

  public static getNormalFromRectangle(rectangle3D: Rectangle3D): Vector3 {
    if (!rectangle3D) {
      return;
    }
    const face: Vector3[] = rectangle3D.toFace();
    const point1: Vector3 = face[0];
    const point2: Vector3 = face[1];
    const point3: Vector3 = face[2];
    return this.getNormalFromFirstPoint(point1, point2, point3);
  }

  public static getNormalFromFirstPoint(point1: Vector3, point2: Vector3, point3: Vector3): Vector3 {
    const normal: Vector3 = this.calculateConnectorFrom3Points(point1, point2, point3, new Vector3(0, 0, 0),
      this.DEFAULT_CONNECTOR_DISTANCE);
    normal.normalize();
    return normal;
  }

  public static calculateConnectorFrom3Points(point1: Vector3, point2: Vector3, point3: Vector3, mid: Vector3, distance: number): Vector3 {
    const plane: Plane = new Plane();
    plane.setFromCoplanarPoints(point1.clone(), point2.clone(), point3.clone());
    return this.getConnectorPoint(plane, mid, distance);
  }

  public static getConnectorPoint(plane: Plane, mid: Vector3, distance: number): Vector3 {
    const n: Vector3 = new Vector3();
    n.x = this.getConnectorX(plane, mid, distance);
    n.y = this.getConnectorY(plane, mid, distance);
    n.z = this.getConnectorZ(plane, mid, distance);
    return n;
  }

  private static getConnectorX(plane: Plane, mid: Vector3, distance: number): number {
    return mid.x + (plane.normal.x * distance) / this.getPlaneSqr(plane);
  }

  private static getConnectorY(plane: Plane, mid: Vector3, distance: number): number {
    return mid.y + (plane.normal.y * distance) / this.getPlaneSqr(plane);
  }

  private static getConnectorZ(plane: Plane, mid: Vector3, distance: number): number {
    return mid.z + (plane.normal.z * distance) / this.getPlaneSqr(plane);
  }

  private static getPlaneSqr(plane: Plane): number {
    return Math.sqrt(plane.normal.x * plane.normal.x + plane.normal.y * plane.normal.y + plane.normal.z * plane.normal.z);
  }

  public static equalsNumber3D(fPoint: Vector3, sPoint: Vector3, epsilon: number = NaN): boolean {
    if (!fPoint || !sPoint) {
      return false;
    }
    const distance: number = fPoint.distanceTo(sPoint);
    return distance < (!NumberUtils.isNumber(epsilon) ? CommonConstants.EPSILON : epsilon);
  }

  public static getNormalsRotationMatrix(normalFrom: Vector3, normalTo: Vector3, epsilon: number = NaN): Matrix4 {
    if (!NumberUtils.isNumber(epsilon)) {
      epsilon = CommonConstants.EPSILON;
    }
    const normalized1: Vector3 = new Vector3(normalTo.x, normalTo.y, normalTo.z);
    normalized1.normalize();
    const normalized2: Vector3 = new Vector3(normalFrom.x, normalFrom.y, normalFrom.z);
    normalized2.normalize();
    const matrix: Matrix4 = new Matrix4();
    const sign: Vector3 = normalized1.clone().cross(normalized2);

    const angle: number = this.getAngleWithNormal(normalized1, normalized2, sign);
    if (Math.abs(angle) < epsilon) {
      // if angle between two vertecies is 0 then there is no rotation
      // do nothing
    } else if (Math.abs(Math.PI - angle) < epsilon) {
      // if angle between two vertecies is 180.
      const A: number = normalized1.x;
      const B: number = normalized1.y;
      const C: number = normalized1.z;
      // normals on the same line, then we take perpendicular plane and any line of that plane
      let z: number;
      let y: number;
      let x: number;
      if (A !== 0) {
        z = 0;
        y = 1;
        x = -B / A;
      } else if (B !== 0) {
        z = 0;
        x = 1;
        y = -A / B;
      } else if (C !== 0) {
        x = 0;
        y = 1;
        z = -B / C;
      }
      matrix.makeRotationAxis(new Vector3(x, y, z).normalize(), MathUtils.toRadians(180));
    } else {
      const bissectrissa: Vector3 = normalized1.clone().add(normalized2);
      bissectrissa.normalize();
      matrix.makeRotationAxis(new Vector3(bissectrissa.x, bissectrissa.y, bissectrissa.z), MathUtils.toRadians(180));
    }
    return matrix;
  }

  public static getAngleWithNormal(v1: Vector3, v2: Vector3, normal: Vector3): number {
    const plane: Plane = new Plane();
    plane.setFromCoplanarPoints(new Vector3(0, 0, 0), v1, v2);
    const clasificator: number = this.classifyPoint(normal, plane);
    let tempAngle: number = v1.angleTo(v2);
    if (!NumberUtils.isNumber(tempAngle)) {
      tempAngle = this.excludeZeroValues(v1).angleTo(this.excludeZeroValues(v2));
    }
    if (!NumberUtils.isNumber(tempAngle)) {
      tempAngle = 0;
    }
    return clasificator === PlaneClassification.BACK ? -tempAngle : tempAngle;
  }

  private static excludeZeroValues(vector: Vector3): Vector3 {
    const eps: number = 0.0001;
    const xValue: number = MathUtils.compare(vector.x, 0, eps) === 0 ? eps : vector.x;
    const yValue: number = MathUtils.compare(vector.y, 0, eps) === 0 ? eps : vector.y;
    const zValue: number = MathUtils.compare(vector.z, 0, eps) === 0 ? eps : vector.z;
    return new Vector3(xValue, yValue, zValue);
  }

  public static compare(a: number, b: number, epsilon: number = NaN): number {
    if (Math.abs(a - b) < (!NumberUtils.isNumber(epsilon) ? CommonConstants.EPSILON : epsilon)) {
      return 0;
    }
    return (a > b ? 1 : -1);
  }

  public static classifyPoint(point: Vector3, plane: Plane, epsilon: number = 0.01): number {
    // check NaN
    const len: number = plane.distanceToPoint(point);
    if (len < -epsilon) {
      return PlaneClassification.BACK;
    } else if (len > epsilon) {
      return PlaneClassification.FRONT;
    } else {
      return PlaneClassification.INTERSECT;
    }
  }

  public static getRotationMatrix(fromFace: Vector3[], toFace: Vector3[], inverseNormal: boolean = true,
    additionalRotationAngle: number = NaN, epsilon: number = NaN, roundUVAngle: boolean = true): Matrix4 {
    fromFace = [fromFace[0].clone(), fromFace[1].clone(), fromFace[2].clone()];
    toFace = [toFace[0].clone(), toFace[1].clone(), toFace[2].clone()];

    // remove scaling
    const dx_0_1: number = fromFace[0].distanceTo(fromFace[1]);
    const dx_0_2: number = fromFace[0].distanceTo(fromFace[2]);
    fromFace[1] = MathUtils.getInterpolated(fromFace[1], fromFace[0], 1 / dx_0_1);
    fromFace[2] = MathUtils.getInterpolated(fromFace[2], fromFace[0], 1 / dx_0_2);

    const dy_0_1: number = toFace[0].distanceTo(toFace[1]);
    const dy_0_2: number = toFace[0].distanceTo(toFace[2]);

    toFace[1] = MathUtils.getInterpolated(toFace[1], toFace[0], 1 / dy_0_1);
    toFace[2] = MathUtils.getInterpolated(toFace[2], toFace[0], 1 / dy_0_2);

    // change position to (0, 0, 0)
    let movement: Vector3 = toFace[0].clone();
    toFace[0] = toFace[0].clone().sub(movement);
    toFace[1] = toFace[1].clone().sub(movement);
    toFace[2] = toFace[2].clone().sub(movement);

    movement = fromFace[0].clone();
    fromFace[0] = fromFace[0].clone().sub(movement);
    fromFace[1] = fromFace[1].clone().sub(movement);
    fromFace[2] = fromFace[2].clone().sub(movement);

    // toFace - is static rectangle and should not be rotated
    const normal1: Vector3 = MathUtils.getNormalFromFirstPoint(toFace[0], toFace[1], toFace[2]);
    const normal2: Vector3 = MathUtils.getNormalFromFirstPoint(fromFace[0], fromFace[1], fromFace[2]);

    // rotate second normal to first normal
    let mat: Matrix4;
    if (inverseNormal) {
      mat = MathUtils.getNormalsRotationMatrix(normal2, new Vector3(-normal1.x, -normal1.y, -normal1.z), epsilon);
    } else {
      mat = MathUtils.getNormalsRotationMatrix(normal2, normal1, epsilon);
    }

    const fromFacer: Vector3[] = MathUtils.transformVectors(fromFace, mat);

    let angleUV: number;
    if (inverseNormal) {
      // rotate U axis to V.
      angleUV = this.getAngleWithNormal(toFace[1], fromFacer[2], new Vector3(-normal1.x, -normal1.y, -normal1.z));
    } else {
      // rotate U axis to U.
      angleUV = this.getAngleWithNormal(toFace[1], fromFacer[1], new Vector3(-normal1.x, -normal1.y, -normal1.z));
    }

    // if the angle is near zero, then set it to zero
    if (roundUVAngle && Math.abs(angleUV) < CommonConstants.EPSILON) {
      angleUV = 0;
    }

    const mat2: Matrix4 = new Matrix4();
    let rotationAngle: number = angleUV * 180 / Math.PI;
    if (NumberUtils.isNumber(additionalRotationAngle)) {
      rotationAngle += additionalRotationAngle;
    }

    mat2.makeRotationAxis(new Vector3(normal1.x, normal1.y, normal1.z), MathUtils.toRadians(rotationAngle));

    mat2.multiply(mat);
    return mat2;
  }

  public static transformVectors(a: Vector3[], matrix: Matrix4): Vector3[] {
    const res: Vector3[] = this.copyNumber3Ds(a);
    for (const n of res) {
      n.copy(MathUtils.transform(n, matrix));
    }
    return res;
  }

  public static copyNumber3Ds(a: Vector3[]): Vector3[] {
    const res: Vector3[] = [];
    for (const n of a) {
      res.push(n.clone());
    }
    return res;
  }

  public static roundNumberValue(value: number, fractionDigits: number = 2, threshold: number = 0.5): number {
    if (fractionDigits < 0) {
      return value;
    }
    const multiplier: number = Math.pow(10, fractionDigits);
    let result: number;
    if (threshold === 0.5) {
      result = Math.round(value * multiplier) / multiplier;
    } else {
      result = this.round(value * multiplier, threshold) / multiplier;
    }
    return result === 0 ? 0 : result;
  }

  public static round(a: number, threshold: number): number {
    const di: number = Math.floor(a);
    if ((a - di) > threshold) {
      return di + 1;
    } else {
      return di;
    }
  }

  /**
   * @return true if array contains given vector, otherwise false
   */
  public static isArrayContainsVector3D(points: Vector3[], searchPoint: Vector3): boolean {
    if (ArrayUtils.isEmpty(points)) {
      return false;
    }
    for (const point of points) {
      if (this.equalsNumber3D(point, searchPoint)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Calculates the least common multiple of two striclty positive integers.
   */
  public static calculateLcm(x1: number, x2: number): number {
    if (x1 <= 0 || x2 <= 0) {
      return NaN;
    }

    let max: number;
    let min: number;
    if (x1 > x2) {
      max = x1;
      min = x2;
    } else {
      max = x2;
      min = x1;
    }

    for (let i: number = 1; i <= min; i++) {
      if ((max * i) % min === 0) {
        return i * max;
      }
    }
    return NaN;
  }

  /**
   * Calculates the least common multiple of several positive integer numbers.
   *
   * @param arr Array containing the numbers
   */
  public static calculateLcmForIntArray(arr: number[]): number {
    if (!arr || arr.length === 0) {
      return 0;
    }
    if (arr.length === 1) {
      return arr[0];
    }

    let tmp: number = this.calculateLcm(arr[arr.length - 1], arr[arr.length - 2]);
    for (let i: number = arr.length - 3; i >= 0; i--) {
      if (arr[i] <= 0) {
        return NaN;
      }
      tmp = this.calculateLcm(tmp, arr[i]);
    }
    return tmp;
  }

  /**
   * Creates array of bounding box points by the provided points.
   */
  public static calculateBoundingPoints(points: Vector3[]): Vector3[] {
    let minX: number = Number.MAX_VALUE;
    let maxX: number = -Number.MAX_VALUE;
    let minY: number = Number.MAX_VALUE;
    let maxY: number = -Number.MAX_VALUE;
    let minZ: number = Number.MAX_VALUE;
    let maxZ: number = -Number.MAX_VALUE;

    for (const point of points) {
      if (point.x < minX) {
        minX = point.x;
      }
      if (point.x > maxX) {
        maxX = point.x;
      }
      if (point.y < minY) {
        minY = point.y;
      }
      if (point.y > maxY) {
        maxY = point.y;
      }
      if (point.z < minZ) {
        minZ = point.z;
      }
      if (point.z > maxZ) {
        maxZ = point.z;
      }
    }

    const boundingPoints: Vector3[] = [];
    boundingPoints.push(new Vector3(minX, minY, minZ));
    boundingPoints.push(new Vector3(minX, minY, maxZ));
    boundingPoints.push(new Vector3(minX, maxY, minZ));
    boundingPoints.push(new Vector3(minX, maxY, maxZ));
    boundingPoints.push(new Vector3(maxX, minY, minZ));
    boundingPoints.push(new Vector3(maxX, minY, maxZ));
    boundingPoints.push(new Vector3(maxX, maxY, minZ));
    boundingPoints.push(new Vector3(maxX, maxY, maxZ));

    return boundingPoints;
  }

  /**
   * Provides 3D projection of the <b>point</b> on line, which is defined by two points: <b>linePoint1</b> and <b>linePoint2</b>
   *
   * @param linePoint1 first line point
   * @param linePoint2 second line point
   * @param point point to check
   * @return projected point
   *
   */
  public static closestPointOnLine(linePoint1: Vector3, linePoint2: Vector3, point: Vector3): Vector3 {
    /*
     V = normalize(A-B);
     X = B + V * dot(V, P-B);
     dot(A, B) = A.x*B.x + A.y*B.y + A.z*B.z
     */
    const v: Vector3 = new Vector3(linePoint1.x - linePoint2.x, linePoint1.y - linePoint2.y, linePoint1.z - linePoint2.z);
    v.normalize();
    const p_b: Vector3 = new Vector3(point.x - linePoint2.x, point.y - linePoint2.y, point.z - linePoint2.z);
    const dot: number = v.dot(p_b);
    const x: Vector3 = new Vector3(linePoint2.x + v.x * dot, linePoint2.y + v.y * dot, linePoint2.z + v.z * dot);
    return x;
  }

  /**
   * Method changes rectangle width by given offset (interpolates 1-st and 2-nd rectangle points).
   */
  public static interpolateRectangle(rectangle: Rectangle3D, offset: number, isLeftSideForInterpolate: boolean = true): void {
    if (!NumberUtils.isNumber(offset) || offset === 0) {
      return;
    }
    const newRectangleWidth: number = rectangle.width - offset;
    const rectangleWidth: number = rectangle.width;
    if (newRectangleWidth <= 0) {
      return;
    }
    if (isLeftSideForInterpolate) {
      rectangle.point1 = MathUtils.getInterpolated(rectangle.point1, rectangle.point4, newRectangleWidth / rectangleWidth);
      rectangle.point2 = MathUtils.getInterpolated(rectangle.point2, rectangle.point3, newRectangleWidth / rectangleWidth);
    } else {
      rectangle.point3 = MathUtils.getInterpolated(rectangle.point3, rectangle.point2, newRectangleWidth / rectangleWidth);
      rectangle.point4 = MathUtils.getInterpolated(rectangle.point4, rectangle.point1, newRectangleWidth / rectangleWidth);
    }
  }

  /**
   * Fills the 3d number object with the result of a 3d matrix tranformation performed on a 3d vector.
   *
   * @param v The 3d vector object to use in the calculation.
   * @param m The 3d matrix object representing the tranformation.
   */
  public static transform(v: Vector3, m: Matrix4): Vector3 {
    if (!v || !m) {
      return null;
    }
    return v.applyMatrix4(m);
  }

  /**
   * Calculate connector point from face. Connector point - point on some distance on the normal, which starts in provided middle point
   *
   * @param geometry face
   * @param mid middle point
   * @param distance distance on the normal
   * @return connector point
   *
   */
  public static calculateConnectorFromFace(geometry: BufferGeometry, mid: Vector3, distance: number = this.DEFAULT_CONNECTOR_DISTANCE): Vector3 {
    const firstFaceVertices: Vector3[] = this.getFirstFaceVertices(geometry);
    const positionA: Vector3 = firstFaceVertices[0].clone();
    const positionB: Vector3 = firstFaceVertices[1].clone();
    const positionC: Vector3 = firstFaceVertices[2].clone();
    return this.calculateConnectorFrom3Points(positionA, positionB, positionC, mid, distance);
  }

  private static getFirstFaceVertices(geometry: BufferGeometry): Vector3[] {
    if (!geometry) {
      return [];
    }
    const firstFace: Face3 = GeometryUtils.getFaces(geometry)[0];
    if (firstFace) {
      const vertices: Vector3[] = GeometryUtils.getVertices(geometry);
      return [vertices[firstFace.a], vertices[firstFace.b], vertices[firstFace.c]];
    }
    return [];
  }

  public static isPointInsideRectangle(rectangle: Rectangle3D, point: Vector3): boolean {
    const topBorder: Line = new Line(rectangle.point1, rectangle.point4);
    const bottomBorder: Line = new Line(rectangle.point2, rectangle.point3);
    const topPointOnLine: Vector3 = this.closestPointOnLine(topBorder.start, topBorder.end, point);
    const bottomPointOnLine: Vector3 = this.closestPointOnLine(bottomBorder.start, bottomBorder.end, point);

    if (!topBorder.hasPoint(topPointOnLine) || !bottomBorder.hasPoint(bottomPointOnLine)) {
      return false;
    }
    return this.compare(point.distanceTo(topPointOnLine) + point.distanceTo(bottomPointOnLine),
        rectangle.point1.distanceTo(rectangle.point2)) === 0;
  }

  public static calculateMiddlePoint(point1: Vector3, point2: Vector3): Vector3 {
    return new Vector3((point1.x + point2.x) / 2, (point1.y + point2.y) / 2, (point1.z + point2.z) / 2);
  }

  public static movePointBetweenContainers(point: Vector3, source: Object3D, destination: Object3D): void {
    const res: Vector3 = destination.worldToLocal(source.localToWorld(point));
    point.copy(res);
  }

  /**
   * Performs move of the provided points
   *
   * @param a points array
   * @param direction move direction
   * @return resulting points
   *
   */
  public static move(a: Vector3[], direction: Vector3): Vector3[] {
    const res: Vector3[] = this.copyNumber3Ds(a);
    for (const n of res) {
      n.add(direction);
    }
    return res;
  }

  public static localToGlobal(object: Object3D, point: Vector3): Vector3 {
    const result: Vector3 = new Vector3(point.x, point.y, point.z);
    if (!object) {
      return result;
    }
    object.updateMatrixWorld(true);
    object.localToWorld(result);
    return result;
  }

  /**
   * Convert euler numbers, to rotation matrix
   *
   * @param euler euler coordinates
   * @return rotation matrix
   *
   */
  public static euler2matrix(euler: Vector3): Matrix4 {
    const quaternion: Quaternion = this.fromEulerAngles(euler.x, euler.y, euler.z);
    const rotationMatrix: Matrix4 = new Matrix4().makeRotationFromQuaternion(quaternion);
    return rotationMatrix;
  }

  public static fromEulerAngles(ax: number, ay: number, az: number): Quaternion {
    const halfX: number = ax * .5, halfY: number = ay * .5, halfZ: number = az * .5;
    const cosX: number = Math.cos(halfX), sinX: number = Math.sin(halfX);
    const cosY: number = Math.cos(halfY), sinY: number = Math.sin(halfY);
    const cosZ: number = Math.cos(halfZ), sinZ: number = Math.sin(halfZ);

    const w: number = cosX * cosY * cosZ + sinX * sinY * sinZ;
    const x: number = sinX * cosY * cosZ - cosX * sinY * sinZ;
    const y: number = cosX * sinY * cosZ + sinX * cosY * sinZ;
    const z: number = cosX * cosY * sinZ - sinX * sinY * cosZ;
    return new Quaternion(x, y, z, w);
  }

  /**
   * Calculate connector point from rectangle. Connector point - point on some distance on the normal,
   * which starts in the center of the rectangle
   *
   * @param rect rectangle
   * @param distance distance on the normal
   * @return connector point
   *
   */
  public static calculateConnectorFromRect(rect: Rectangle3D, distance: number = this.DEFAULT_CONNECTOR_DISTANCE): Vector3 {
    let mid: Vector3 = new Vector3(rect.point1.x, rect.point1.y, rect.point1.z);
    mid = MathUtils.getInterpolated(mid, rect.point3, 0.5);
    return this.calculateConnectorFrom3Points(rect.point1, rect.point2, rect.point3, mid, distance);
  }

  /**
   * This method returns projection point of a plane.
   *
   * @param p1 first point on plane
   * @param p2 second point on plane
   * @param p3 third point on plane
   * @param point point for which you want to find a projection
   *
   * @return Vector3D
   */
  public static getProjectionPointOfPlane(p1: Vector3, p2: Vector3, p3: Vector3, point: Vector3): Vector3 {
    const plane: Plane = new Plane();
    plane.setFromCoplanarPoints(p1, p2, p3);
    return plane.projectPoint(point.clone(), new Vector3());
  }

  public static scaleVector(vector: Vector3, pivotPoint: Vector3, scaleMatrix: Matrix4): Vector3 {
    let result: Vector3 = vector.clone().sub(pivotPoint);
    result = result.applyMatrix4(scaleMatrix);
    result.add(pivotPoint);
    return result;
  }

  /**
   * Returns offset to move object by given distance in the direction of base line.
   *
   * @param distance offset length
   * @param baseLine base line which indicates offset direction
   * @return offset
   *
   */
  public static calculateOffset(distance: number, baseLine: Line): Vector3 {
    const baseDistance: number = baseLine.length;
    const offset: Vector3 = baseLine.end.clone().sub(baseLine.start);
    offset.multiplyScalar(distance / baseDistance);
    return offset;
  }

  public static rotateRectangle(rectangle: Rectangle3D, angle: number): void {
    const normal: Vector3 = MathUtils.getNormalFromFirstPoint(rectangle.point1, rectangle.point2, rectangle.point3);
    const middleVector: Vector3 = this.calculateMiddlePointByRectangle(rectangle);

    const points: Vector3[] = this.copyNumber3Ds(rectangle.toArray());
    for (const point of points) {
      point.sub(middleVector);
      point.applyAxisAngle(normal, MathUtils.toRadians(angle));
      point.add(middleVector);
    }
    rectangle.point1 = points[0];
    rectangle.point2 = points[1];
    rectangle.point3 = points[2];
    rectangle.point4 = points[3];
  }

  public static copyNumber3D(n: Vector3): Vector3 {
    return n.clone();
  }

  public static rotate(a: Vector3[], matrix: Matrix4): Vector3[] {
    const res: Vector3[] = this.copyNumber3Ds(a);
    for (const n of res) {
      n.copy(MathUtils.rotateVector(n, matrix));
    }
    return res;
  }

  public static rotateVector(v: Vector3, m: Matrix4): Vector3 {
    if (!v || !m) {
      return null;
    }
    return v.applyMatrix4(m);
  }

  public static localToGlobalArray(container: Object3D, points: Vector3[]): Vector3[] {
    const globalArray: Vector3[] = [];
    for (const point of points) {
      globalArray.push(this.localToGlobal(container, point));
    }
    return globalArray;
  }

  public static calculateConnectionRecsDistanceOnLine(connection1Rec: Rectangle3D, connection2Rec: Rectangle3D, line: Line,
                                                      useMiddlePoint: boolean = true): number {
    const startPoint: Vector3 = MathUtils.getInterpolated(line.start, line.end,
      CommonConstants.CONSTANT_OFFSET / line.start.distanceTo(line.end));
    const connectionPoint1: Vector3 = useMiddlePoint ? this.calculateMiddlePointByRectangle(connection1Rec) : connection1Rec.point1;
    const connectionPoint2: Vector3 = useMiddlePoint ? this.calculateMiddlePointByRectangle(connection2Rec) : connection2Rec.point1;

    const closestPoint1: Vector3 = this.closestPointOnLine(line.start, line.end, connectionPoint1);
    const closestPoint2: Vector3 = this.closestPointOnLine(line.start, line.end, connectionPoint2);

    return closestPoint2.distanceTo(startPoint) - closestPoint1.distanceTo(startPoint);
  }

  public static isEqualsVectorsByXZAxis(firstPoint: Vector3, secondPoint: Vector3, epsilon: number = NaN): boolean {
    return MathUtils.compare(firstPoint.x, secondPoint.x, epsilon) === 0 && MathUtils.compare(firstPoint.z, secondPoint.z, epsilon) === 0;
  }

  public static isEqualsVectorsByTwoAxis(firstPoint: Vector3, secondPoint: Vector3, epsilon: number = NaN): boolean {
    return (this.isEqualsVectorsByXYAxis(firstPoint, secondPoint, epsilon)
    || this.isEqualsVectorsByXZAxis(firstPoint, secondPoint, epsilon)
    || this.isEqualsVectorsByYZAxis(firstPoint, secondPoint, epsilon));
  }

  public static isEqualsVectorsByXYAxis(firstPoint: Vector3, secondPoint: Vector3, epsilon: number = NaN): boolean {
    return MathUtils.compare(firstPoint.x, secondPoint.x, epsilon) === 0 && MathUtils.compare(firstPoint.y, secondPoint.y, epsilon) === 0;
  }

  public static isEqualsVectorsByYZAxis(firstPoint: Vector3, secondPoint: Vector3, epsilon: number = NaN): boolean {
    return MathUtils.compare(firstPoint.z, secondPoint.z, epsilon) === 0 && MathUtils.compare(firstPoint.y, secondPoint.y, epsilon) === 0;
  }

  /**
   * Method shifts rectangle points by offset in the given direction.
   *
   * @param rectangle rectangle
   * @param offset offset
   * @param direction direction (e.g. "U" or "V")
   */
  public static moveRectangle(rectangle: Rectangle3D, offset: number, direction: string): void {
    const points: Vector3[] = this.copyNumber3Ds(rectangle.toArray());
    switch (direction) {
      case CommonConstants.DIRECTION_U:
        rectangle.point1 = MathUtils.getInterpolated(points[0], points[1],
          (points[0].distanceTo(points[1]) - offset) / points[0].distanceTo(points[1]));
        rectangle.point2 = MathUtils.getInterpolated(points[1], points[0],
          (points[0].distanceTo(points[1]) + offset) / points[0].distanceTo(points[1]));
        rectangle.point3 = MathUtils.getInterpolated(points[2], points[3],
          (points[2].distanceTo(points[3]) + offset) / points[2].distanceTo(points[3]));
        rectangle.point4 = MathUtils.getInterpolated(points[3], points[2],
          (points[2].distanceTo(points[3]) - offset) / points[2].distanceTo(points[3]));
        return;
      case CommonConstants.DIRECTION_V:
        rectangle.point1 = MathUtils.getInterpolated(points[0], points[3],
          (points[0].distanceTo(points[3]) - offset) / points[0].distanceTo(points[3]));
        rectangle.point2 = MathUtils.getInterpolated(points[1], points[2],
          (points[1].distanceTo(points[2]) - offset) / points[1].distanceTo(points[2]));
        rectangle.point3 = MathUtils.getInterpolated(points[2], points[1],
          (points[1].distanceTo(points[2]) + offset) / points[1].distanceTo(points[2]));
        rectangle.point4 = MathUtils.getInterpolated(points[3], points[0],
          (points[0].distanceTo(points[3]) + offset) / points[0].distanceTo(points[3]));
        return;
      case CommonConstants.DIRECTION_N:
        const normal: Vector3 = this.getNormalFromFirstPoint(points[0], points[1], points[2]);
        rectangle.point1 = new Vector3(points[0].x + normal.x * offset, points[0].y + normal.y * offset, points[0].z + normal.z * offset);
        rectangle.point2 = new Vector3(points[1].x + normal.x * offset, points[1].y + normal.y * offset, points[1].z + normal.z * offset);
        rectangle.point3 = new Vector3(points[2].x + normal.x * offset, points[2].y + normal.y * offset, points[2].z + normal.z * offset);
        rectangle.point4 = new Vector3(points[3].x + normal.x * offset, points[3].y + normal.y * offset, points[3].z + normal.z * offset);
    }
  }

  public static globalToLocal(object: Object3D, point: Vector3): Vector3 {
    const result: Vector3 = new Vector3(point.x, point.y, point.z);
    if (!object) {
      return result;
    }
    object.updateMatrixWorld(true);
    object.worldToLocal(result);
    return result;
  }

  public static equalsNumber(n1: number, n2: number, epsilon: number = NaN): boolean {
    if (NumberUtils.isNotNumber(n1)) {
      return NumberUtils.isNotNumber(n2);
    }
    if (NumberUtils.isNotNumber(n2)) {
      return false;
    }
    return Math.abs(n2 - n1) < (NumberUtils.isNotNumber(epsilon) ? CommonConstants.EPSILON : epsilon);
  }

  public static equalsRectangle3D(fRectangle: Rectangle3D, sRectangle: Rectangle3D, epsilon: number = NaN): boolean {
    return this.equalsNumber3D(fRectangle.point1, sRectangle.point1, epsilon)
      && this.equalsNumber3D(fRectangle.point2, sRectangle.point2, epsilon)
      && this.equalsNumber3D(fRectangle.point3, sRectangle.point3, epsilon)
      && this.equalsNumber3D(fRectangle.point4, sRectangle.point4, epsilon);
  }

  public static applyObjectTransformationToRectangle(rectangle: Rectangle3D, object: Object3D): void {
    if (!rectangle || !object) {
      return;
    }
    object.updateMatrix();
    const transformationMatrix: Matrix4 = object.matrix;
    rectangle.point1.applyMatrix4(transformationMatrix);
    rectangle.point2.applyMatrix4(transformationMatrix);
    rectangle.point3.applyMatrix4(transformationMatrix);
    rectangle.point4.applyMatrix4(transformationMatrix);
  }

  public static isRectanglesNormalsEqual(rectangle1: Rectangle3D, rectangle2: Rectangle3D): boolean {
    const connectionHeightNormal: Vector3 = rectangle1.point2.clone().sub(rectangle1.point1).normalize();
    const nextConnectionHeightNormal: Vector3 = rectangle2.point2.clone().sub(rectangle2.point1).normalize();
    return MathUtils.equalsNumber3D(connectionHeightNormal, nextConnectionHeightNormal);
  }

  public static isRectanglesVerticallyAligned(rectangle1: Rectangle3D, rectangle2: Rectangle3D): boolean {
    const plane1: Plane = new Plane().setFromCoplanarPoints(rectangle1.point1, rectangle1.point2, rectangle1.point3);
    const plane2: Plane = new Plane().setFromCoplanarPoints(rectangle2.point1, rectangle2.point2, rectangle2.point3);
    return MathUtils.equalsNumber3D(plane1.normal, plane2.normal) && MathUtils.equalsNumber(plane1.constant, plane2.constant);
  }

  public static calculateDistanceBetweenRectangles(rectangle1: Rectangle3D, rectangle2: Rectangle3D): number {
    const mid1: Vector3 = MathUtils.calculateMiddlePointByRectangle(rectangle1);
    const mid2: Vector3 = MathUtils.calculateMiddlePointByRectangle(rectangle2);
    const bbox1: Box3 = new Box3().setFromPoints(rectangle1.toArray());
    const bbox2: Box3 = new Box3().setFromPoints(rectangle2.toArray());
    const direction: Vector3 = mid2.clone().sub(mid1).normalize().round();
    mid1.add(bbox1.getSize(new Vector3()).multiplyScalar(0.5).multiply(direction));
    mid2.sub(bbox2.getSize(new Vector3()).multiplyScalar(0.5).multiply(direction));
    return mid1.distanceTo(mid2);
  }

  public static clamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max);
  }

  /**
   * Check whether two rectangles are congruent within a difference of epsilon.
   * Horizontally and or vertically flipped rectangles are also considered congruent.
   * @param a the first rectangle
   * @param b the second rectangle
   * @param epsilon the maximal allowed difference
   * @returns true, if the rectangles are identical within the given epsilon, false otherwise
   */
  public static areRectanglesCongruent(a: Rectangle3D, b: Rectangle3D, epsilon: number = NaN): boolean {
    // check four orientations of the second rectangle:
    return this.equalsRectangle3D(a, b, epsilon)
      || this.equalsRectangle3D(a, b.clone().flipHorizontal(), epsilon)
      || this.equalsRectangle3D(a, b.clone().flipVertical(), epsilon)
      || this.equalsRectangle3D(a, b.clone().flipHorizontal().flipVertical(), epsilon)
  }

    /** rotates an object around a line (defined by pointA and B) by the given angle in radians */
  public static rotateObjectAroundLine(object: Object3D, pointA: Vector3, pointB: Vector3, angleRadians: number) {
    const axis = new Vector3().subVectors(pointB, pointA).normalize();
    const quaternion = new Quaternion().setFromAxisAngle(axis, angleRadians);
    object.position.sub(pointA);
    object.position.applyQuaternion(quaternion);
    object.position.add(pointA);
    object.quaternion.premultiply(quaternion);
  }
}
